import {chain} from 'lodash';
import React, {FC, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SearchInboxDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/search/searchInboxDropdown';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {FilterApplyFooter} from '../../../components/common/filters/common/filterApplyFooter';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getUserParticipatingInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {LIVE_DASHBOARD_MAX_INBOXES} from './constants';

interface LiveDashboardInboxesDropdownProps {
  namespace: string;

  selectedInboxIds: ReadonlyArray<number>;
  setSelectedInboxIds: (selection: ReadonlyArray<number>) => void;

  popoverProps: PopoverTransferProps;
}

export const LiveDashboardInboxesDropdown: FC<LiveDashboardInboxesDropdownProps> = (props) => {
  const {namespace, selectedInboxIds, setSelectedInboxIds, popoverProps} = props;

  const [inboxIds, setInboxIds] = useState(selectedInboxIds);

  // We only allow selecting inboxes the user has access to.
  const userParticipatingInboxes = useAppSelector(getUserParticipatingInboxes);
  const userParticipatingInboxesInNamespace = chain(userParticipatingInboxes)
    .filter((inbox) => inbox.namespace === namespace)
    // Place selected inboxes first. This relies on sortBy's stability.
    .sortBy((inbox) => !selectedInboxIds.includes(inbox.id))
    .value();

  // After reaching the max inbox count, prevent selecting additional inboxes.
  const disabledInboxIds =
    inboxIds.length >= LIVE_DASHBOARD_MAX_INBOXES
      ? userParticipatingInboxesInNamespace.map((inbox) => inbox.id).filter((id) => !inboxIds.includes(id))
      : undefined;

  return (
    <SearchInboxDropdown
      {...popoverProps}
      inboxes={userParticipatingInboxesInNamespace}
      selectedInboxIds={inboxIds}
      headerTitle={<Trans id="dropdown-shared-inboxes-title">Shared inboxes</Trans>}
      isMultiSelect
      footer={
        <FilterApplyFooter
          isApplyDisabled={inboxIds.length === 0}
          onApplyClicked={() => {
            setSelectedInboxIds(inboxIds);
            popoverProps.onRequestClose();
          }}
          onCancelClicked={popoverProps.onRequestClose}
          renderApplyDisabledTooltip={() => (
            <Trans id="live-dashboard-inboxes-dropdown-disabled">Select at least 1 inbox</Trans>
          )}
        />
      }
      disabledInboxIds={disabledInboxIds}
      renderDisabledLabel={() => (
        <Trans id="live-dashboard-max-inboxes-tooltip">
          Maximum {LIVE_DASHBOARD_MAX_INBOXES} inboxes can be selected
        </Trans>
      )}
      headerRightIcon={
        <Button
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={() => setInboxIds([])}
        >
          <Trans id="simple-dropdown-header-clear-button-label">Clear</Trans>
        </Button>
      }
      onChange={(inbox: InboxModel) => {
        if (inboxIds.includes(inbox.id)) {
          setInboxIds(inboxIds.filter((id) => id !== inbox.id));
        } else {
          setInboxIds([...inboxIds, inbox.id]);
        }
      }}
    />
  );
};
